import HttpService from '@/common/services/HttpService'
import Keycloak, { type KeycloakLogoutOptions, type KeycloakConfig, type KeycloakLoginOptions } from 'keycloak-js'
import configResolver, { pathCombine } from '@/common/helpers/configResolver'
import { extractErrorMessage } from '@/common/helpers/errorUtils'
import type { AuthenticationConfigDTOV1 } from '@/common/types/authenticationConfig'
import { APP_ROUTE_PATHS } from '@/app/types/router'
import * as NavigationService from '@/common/services/NavigationService'
import {
  clearKeycloakSessionStorage,
  getCurrentKeycloakRefreshToken,
  getCurrentKeycloakToken,
  updateKeycloakSessionStorage,
} from '@/common/services/SessionService'

const AUTH_CONFIG_PATH = '/api/v1/auth/config'
const OIDC_SESSION_LINK_API = '/api/v1/oidc-session-links'

let _disconnecting = false
let keycloakInstanceRef: Keycloak | undefined
let manageOidcSession: boolean // call createOidcSession, updateOidcSession, removeOidcSession

// Function to apply authentication
export const keycloakAuthentication = async (withOidcSession = true): Promise<Keycloak | void> => {
  const authenticationConfig = await getAuthenticationConfig()
  if (!isValidOidcProviderUrl(authenticationConfig.oidcProviderUrl)) return
  const keycloak = await initKeycloak(authenticationConfig)
  if (!keycloak.authenticated) {
    throw new Error('Keycloak authentication failed.')
  }

  keycloak.onAuthLogout = async () => {
    keycloak.loginRequired = false
    await logout()
  }

  keycloakInstanceRef = keycloak
  manageOidcSession = withOidcSession
  if (manageOidcSession && isFirstConnection()) {
    await createOidcSession()
  }
  updateKeycloakSessionStorage(keycloak)
  return keycloak
}

// Function to handle oidc logout
const _oidcLogout = async (options?: KeycloakLogoutOptions) => {
  _disconnecting = true
  try {
    try {
      if (manageOidcSession || keycloakInstanceRef) await removeOidcSession()
      clearKeycloakSessionStorage()
    } catch (error) {
      console.error('Error removeOidcSession: ', error)
    } finally {
      await keycloakInstanceRef?.logout(options)
    }
  } catch (error) {
    console.error('Error keycloak logout : ', error)
  }
}

export const logout = async () => {
  NavigationService.resetNavigationHistory()
  const redirectUri = pathCombine([configResolver.root, APP_ROUTE_PATHS.OIDC_LOGOUT_ROUTE_PATH])
  await _oidcLogout({ redirectUri })
}

const initKeycloak = async (authenticationConfig: AuthenticationConfigDTOV1): Promise<Keycloak> => {
  const keycloakConfig: KeycloakConfig = {
    url: authenticationConfig.oidcProviderUrl,
    realm: authenticationConfig.tenantId,
    clientId: 'web-end-user-client',
  }
  const keycloak = new Keycloak(keycloakConfig)
  const authenticated = await keycloak.init({
    onLoad: 'login-required',
    checkLoginIframe: false,
    token: getCurrentKeycloakToken()!,
    refreshToken: getCurrentKeycloakRefreshToken()!,
  })
  if (authenticated) {
    handleTokenExpired(keycloak)
  }
  return keycloak
}

function isFirstConnection(): boolean {
  return !getCurrentKeycloakToken()?.trim()
}

export async function triggerOidcChangePassword(redirectUri = '') {
  const keycloakLoginOptions: KeycloakLoginOptions = { action: 'UPDATE_PASSWORD', redirectUri: redirectUri, prompt: 'login' }
  await keycloakInstanceRef?.login(keycloakLoginOptions)
}

// init backend session from Keycloak token
const createOidcSession = async () => {
  try {
    await HttpService.post(OIDC_SESSION_LINK_API, {}, { headers: { Authorization: `Bearer ${keycloakInstanceRef?.token}` } })
  } catch (error) {
    const errorMessage = extractErrorMessage(error)
    const errorPage = `${configResolver.root}assist/error?errorMessage=${errorMessage}`
    //remove current keycloak session
    clearKeycloakSessionStorage()
    await keycloakInstanceRef?.logout({ redirectUri: errorPage })
  }
}
// update backend session with Keycloak token
const updateOidcSession = async () => {
  await HttpService.put(OIDC_SESSION_LINK_API)
}
// remove backend session
const removeOidcSession = async () => {
  await HttpService.delete(OIDC_SESSION_LINK_API)
}
// Function to handle token refresh
const handleTokenExpired = (keycloak: Keycloak) => {
  setInterval(async () => {
    try {
      const refreshed = await keycloak.updateToken(60) // If the token expires within 60 seconds
      if (refreshed) {
        updateKeycloakSessionStorage(keycloak)
        if (manageOidcSession) {
          await updateOidcSession()
        }
      }
    } catch (error) {
      console.error('Failed to refresh access token', error)
    }
  }, 60000) // Check access token refresh every 60 seconds
}

// check if oidc provider url is valid
function isValidOidcProviderUrl(oidcUrl: string | undefined | null): boolean {
  return typeof oidcUrl === 'string' && oidcUrl.trim() !== ''
}

// Function to get authentication configuration from backend
function getAuthenticationConfig(): Promise<AuthenticationConfigDTOV1> {
  return HttpService.getData<AuthenticationConfigDTOV1>(AUTH_CONFIG_PATH)
}

export const isAuthenticated = () => keycloakInstanceRef?.authenticated && !_disconnecting
